/**
 * Starter scenes for new free-form diagrams.
 *
 * Elements are written minimally and deliberately: Excalidraw runs everything
 * in `initialData` through its own restore step, which fills in the fields not
 * specified here (seeds, version counters, bindings). Hand-writing the full
 * element shape would be long, fragile, and would go stale when the format
 * version moves.
 */

export type FreeFormTemplateId = "blank" | "mindmap" | "retro" | "architecture";

export type FreeFormTemplate = {
  id: FreeFormTemplateId;
  label: string;
  description: string;
};

export const FREEFORM_TEMPLATES: FreeFormTemplate[] = [
  {
    id: "blank",
    label: "Blank canvas",
    description: "Start from nothing.",
  },
  {
    id: "mindmap",
    label: "Mind map",
    description: "A central topic with four branches to rename.",
  },
  {
    id: "retro",
    label: "Retro board",
    description: "Start, Stop and Continue columns.",
  },
  {
    id: "architecture",
    label: "Architecture",
    description: "A three-tier skeleton with labelled boxes.",
  },
];

/* -------------------------------------------------------------------------- */

let counter = 0;
const nextId = () => `tpl-${Date.now().toString(36)}-${(counter += 1)}`;

const INK = "#1e1e1e";

type Shape = Record<string, unknown>;

function box(
  x: number,
  y: number,
  width: number,
  height: number,
  background = "transparent",
): Shape {
  return {
    id: nextId(),
    type: "rectangle",
    x,
    y,
    width,
    height,
    angle: 0,
    strokeColor: INK,
    backgroundColor: background,
    fillStyle: "solid",
    strokeWidth: 2,
    strokeStyle: "solid",
    roughness: 1,
    opacity: 100,
    groupIds: [],
    roundness: { type: 3 },
    isDeleted: false,
    locked: false,
  };
}

/**
 * Standalone text rather than a container label: bound labels need matching
 * containerId and boundElements entries on both sides, which is easy to get
 * subtly wrong and adds nothing for a starting point someone will edit anyway.
 */
function text(
  x: number,
  y: number,
  value: string,
  { size = 20, width = 200 }: { size?: number; width?: number } = {},
): Shape {
  return {
    id: nextId(),
    type: "text",
    x,
    y,
    width,
    height: size * 1.25,
    angle: 0,
    strokeColor: INK,
    backgroundColor: "transparent",
    fillStyle: "solid",
    strokeWidth: 2,
    strokeStyle: "solid",
    roughness: 1,
    opacity: 100,
    groupIds: [],
    isDeleted: false,
    locked: false,
    text: value,
    originalText: value,
    fontSize: size,
    fontFamily: 1,
    textAlign: "left",
    verticalAlign: "top",
    lineHeight: 1.25,
  };
}

function arrow(x: number, y: number, dx: number, dy: number): Shape {
  return {
    id: nextId(),
    type: "arrow",
    x,
    y,
    width: Math.abs(dx),
    height: Math.abs(dy),
    angle: 0,
    strokeColor: INK,
    backgroundColor: "transparent",
    fillStyle: "solid",
    strokeWidth: 2,
    strokeStyle: "solid",
    roughness: 1,
    opacity: 100,
    groupIds: [],
    isDeleted: false,
    locked: false,
    points: [
      [0, 0],
      [dx, dy],
    ],
  };
}

/* -------------------------------------------------------------------------- */

function mindMap(): Shape[] {
  const branches = [
    { dx: -260, dy: -140, label: "Branch one" },
    { dx: 260, dy: -140, label: "Branch two" },
    { dx: -260, dy: 140, label: "Branch three" },
    { dx: 260, dy: 140, label: "Branch four" },
  ];

  const elements: Shape[] = [
    box(340, 260, 220, 90, "#e7f5ff"),
    text(370, 292, "Central topic", { size: 24 }),
  ];

  for (const { dx, dy, label } of branches) {
    const bx = 450 + dx - 90;
    const by = 305 + dy - 35;
    elements.push(
      arrow(450 + (dx > 0 ? 110 : -110), 305, dx > 0 ? dx - 200 : dx + 200, dy),
      box(bx, by, 180, 70),
      text(bx + 20, by + 24, label, { size: 16, width: 140 }),
    );
  }

  return elements;
}

function retroBoard(): Shape[] {
  const columns = [
    { label: "Start", tint: "#e6f5ee" },
    { label: "Stop", tint: "#fdecec" },
    { label: "Continue", tint: "#eaf1fe" },
  ];

  const elements: Shape[] = [text(120, 90, "Sprint retrospective", { size: 28, width: 400 })];

  columns.forEach(({ label, tint }, index) => {
    const x = 120 + index * 320;
    elements.push(
      box(x, 160, 280, 520, tint),
      text(x + 20, 180, label, { size: 22, width: 240 }),
      text(x + 20, 230, "Double-click to add a note", { size: 14, width: 240 }),
    );
  });

  return elements;
}

function architecture(): Shape[] {
  const tiers = [
    { label: "Presentation", y: 140, tint: "#eaf1fe" },
    { label: "Application", y: 320, tint: "#e6f5ee" },
    { label: "Data", y: 500, tint: "#fdf3e4" },
  ];

  const elements: Shape[] = [text(120, 70, "System architecture", { size: 28, width: 400 })];

  tiers.forEach(({ label, y, tint }, index) => {
    elements.push(
      text(120, y - 32, label, { size: 16, width: 200 }),
      box(120, y, 240, 110, tint),
      box(420, y, 240, 110, tint),
    );
    if (index < tiers.length - 1) {
      elements.push(arrow(240, y + 110, 0, 70), arrow(540, y + 110, 0, 70));
    }
  });

  return elements;
}

const BUILDERS: Record<FreeFormTemplateId, () => Shape[]> = {
  blank: () => [],
  mindmap: mindMap,
  retro: retroBoard,
  architecture,
};

/**
 * A complete .excalidraw scene file for the chosen template, ready to PUT.
 */
export function freeFormTemplateScene(id: FreeFormTemplateId): string {
  return JSON.stringify(
    {
      type: "excalidraw",
      version: 2,
      source: "SCHEmunda",
      elements: BUILDERS[id](),
      appState: { viewBackgroundColor: "#ffffff", gridSize: null },
      files: {},
    },
    null,
    2,
  );
}

/** An empty scene, used when a free-form file is missing or unreadable. */
export const EMPTY_FREEFORM_SCENE = freeFormTemplateScene("blank");
